import { first5Digits, normalizeAddressForComparison } from "@/src/lib/utils/address-normalization";
import { normalizePhoneToNanpDigits } from "@/src/lib/utils/date-and-phone-utils";
import type { AddressParts } from "@/src/models/address";
import type { Patient } from "@/src/models/patient";

/** A contact detail we hold for the patient that the EHR chart doesn't carry. */
export enum PatientContactSyncGap {
  Phone = "phone",
  Email = "email",
  HomeAddress = "homeAddress",
}

/**
 * Whether an address is complete enough for an EHR patient write. A partial address is never sent:
 * `state: ""` is not a code Athena accepts, so it risks losing the whole update. Mirrors the check
 * in `updateAthenaPatient`.
 */
export function isCompleteAddress(address: AddressParts | null | undefined): boolean {
  return Boolean(address?.line1 && address.city && address.state && address.postalCode);
}

/** Words an EHR moves between the two address lines rather than treating as part of the street. */
const UNIT_DESIGNATOR_REGEX = /\b(?:apt|ste|unit|no)\s+/g;

const streetTokens = (address: AddressParts): string[] =>
  normalizeAddressForComparison([address.line1, address.line2].filter(Boolean).join(" "))
    .replace(UNIT_DESIGNATOR_REGEX, "")
    .split(/\s+/)
    .filter(Boolean);

/**
 * Whether every phone number we hold is reachable on the chart, in either slot.
 *
 * Slot-agnostic on purpose: which of home/mobile a number lands in differs between the create and
 * update writes, and ops only care that the number is on the chart at all. A number that won't
 * normalize counts as missing — no US EHR stored it either, which is exactly the case worth
 * flagging, since Athena drops an out-of-NANP number on a 200 with no error.
 */
function isPhoneOnChart(patient: Patient, chartPatient: Patient): boolean {
  const heldNumbers = [patient.mobilePhoneNumber, patient.homePhoneNumber, patient.workPhoneNumber].filter(
    (phone): phone is string => !!phone?.trim()
  );

  if (heldNumbers.length === 0) return true;

  const chartNumbers = new Set(
    [chartPatient.mobilePhoneNumber, chartPatient.homePhoneNumber, chartPatient.workPhoneNumber]
      .map(normalizePhoneToNanpDigits)
      .filter((digits): digits is string => digits !== null)
  );

  return heldNumbers.every((phone) => {
    const digits = normalizePhoneToNanpDigits(phone);

    return digits !== null && chartNumbers.has(digits);
  });
}

/**
 * Whether the address we hold is the one on the chart.
 *
 * The street is compared as one string and only for coverage, because an EHR standardizes what it
 * stores: "123 Main Street Apt 4" comes back as "123 MAIN ST" with "APT 4" moved to the second
 * line. Demanding an exact per-line match would report a gap on an address that synced fine. City,
 * state and zip are compared only where we hold them, so a partial address isn't judged on the
 * parts we never had.
 */
function isHomeAddressOnChart(address: AddressParts | null, chartAddress: AddressParts | null): boolean {
  if (!address?.line1?.trim()) return true;
  if (!chartAddress) return false;

  const chartStreet = new Set(streetTokens(chartAddress));
  const street = streetTokens(address);
  const comparisons = [
    [normalizeAddressForComparison(address.city), normalizeAddressForComparison(chartAddress.city)],
    [normalizeAddressForComparison(address.state), normalizeAddressForComparison(chartAddress.state)],
    [first5Digits(address.postalCode), first5Digits(chartAddress.postalCode)],
  ].filter(([heldValue]) => heldValue !== "");

  return (
    street.length > 0 &&
    street.every((token) => chartStreet.has(token)) &&
    comparisons.every(([heldValue, chartValue]) => heldValue === chartValue)
  );
}

/**
 * Which of the patient's contact details never made it onto the EHR chart.
 *
 * A successful patient write is not evidence that the values landed. Athena drops anything it
 * can't store — an out-of-NANP phone number is its own documented example — and answers 200 with
 * no error field; an address we hold only partially is never sent at all, because a partial
 * address risks the whole write. Either way the chart keeps its old contact details while the
 * workflow reports success, and staff go on calling a number nobody verified.
 *
 * Comparing against a freshly read chart is the only way to tell, and it re-answers itself: once
 * the value is in the EHR, however it got there, the gap closes on its own.
 */
export function findPatientContactSyncGaps(patient: Patient, chartPatient: Patient): PatientContactSyncGap[] {
  const gaps: PatientContactSyncGap[] = [];
  // EHRs store email case-insensitively.
  const email = patient.personalEmail?.trim().toLowerCase();

  if (!isPhoneOnChart(patient, chartPatient)) gaps.push(PatientContactSyncGap.Phone);
  if (email && chartPatient.personalEmail?.trim().toLowerCase() !== email) gaps.push(PatientContactSyncGap.Email);
  if (!isHomeAddressOnChart(patient.homeAddress, chartPatient.homeAddress))
    gaps.push(PatientContactSyncGap.HomeAddress);

  return gaps;
}
